import type { DocumentType } from "@/features/documents/types";
/** Admin bo'limi turlari: backend/apps/accounts/serializers.py, appsettings/models.py, core/audit_views.py */
import type { Permission, Role } from "@/features/auth/types";

export interface AdminUser {
  id: number;
  /** Kirish logini (email yo'q, 2026-10-18) */
  username: string;
  first_name: string;
  last_name: string;
  full_name: string;
  display_name: string;
  initials: string;
  phone: string;
  role: Role;
  branch: number | null;
  branch_name: string | null;
  /** Qo'shimcha doira: menejer -- boshqa filiallar (asosiysidan tashqari) */
  extra_branches: number[];
  extra_branch_names: string[];
  is_blocked: boolean;
  blocked_at: string | null;
  totp_enabled: boolean;
  last_login: string | null;
  drivers_count: number;
  /** Ruxsatlari rol standartidan farq qiladi (ro'yxatda "maxsus" belgisi) */
  custom_access: boolean;
  created_at: string;
  version: number;
}

/** Ruxsatlar oynasi (backend: GET/PATCH /api/users/<id>/access/, accounts/access.py) */
export interface AccessItem {
  key: Permission;
  group: "sections" | "drivers" | "carriers";
  default: boolean;
  value: boolean;
}

export interface UserAccess {
  role: Role;
  editable: boolean;
  items: AccessItem[];
  custom: boolean;
  version: number;
}

export interface UserWrite {
  username: string;
  /** Vaqtinchalik parol -- faqat yaratishda (keyin "Yangi parol berish" amali) */
  password?: string;
  first_name: string;
  last_name: string;
  role: Role;
  branch: number | null;
  extra_branches?: number[];
  phone?: string;
}

export interface BlockResult {
  user: AdminUser;
  drivers_moved: number;
}

export interface Branch {
  id: number;
  name: string;
  code: string;
  timezone: string;
  /** Menejerlar xodimlardan hisoblanadi (asosiy yoki qo'shimcha filiali shu); biriktirish -- xodim formasida */
  managers: PersonRef[];
  address: string;
  working_hours: string;
  recruiters_count: number;
  is_archived: boolean;
  version: number;
}

export interface BranchWrite {
  name: string;
  code: string;
  timezone: string;
  address?: string;
  working_hours?: string;
}

export interface PersonRef {
  id: number;
  display_name: string;
}

export type AssignmentEndPolicy = "extend_if_active" | "return_to_pool" | "ask_manager";
export type DuplicatePolicy = "block_show_owner" | "allow_with_warning" | "block_recruiters";
export type Approver = "owner" | "branch_manager";

/** Owner ko'radigan to'liq sozlamalar (GET /api/settings/) */
export interface AppSettingsFull {
  assignment_days: number;
  assignment_end_policy: AssignmentEndPolicy;
  assignment_activity_window_days: number;
  duplicate_check_phone: boolean;
  duplicate_check_cdl: boolean;
  duplicate_check_email: boolean;
  duplicate_check_phone_secondary: boolean;
  duplicate_check_name: boolean;
  duplicate_policy: DuplicatePolicy;
  rejection_reasons: string[];
  lost_reasons: string[];
  quit_reasons: string[];
  pipeline_stale_days: number;
  bonus_rule_name: string;
  bonus_per_hire: number;
  /** Har recruiter uchun oylik ishga olish maqsadi (widget), 0 -- yo'q */
  monthly_hire_goal: number;
  bonus_per_30_days: number;
  bonus_per_60_days: number;
  bonus_per_90_days: number;
  bonus_currency: string;
  expense_categories: string[];
  /** Kirim kategoriyalari va komissiya foizlari (2026-10-25): {in: {kategoriya: foiz}, out: {...}} */
  income_categories: string[];
  commission_rates: { in: Record<string, number>; out: Record<string, number> };
  expense_receipt_required: boolean;
  expense_approver: Approver;
  expense_auto_approve_limit: number;
  expense_auto_approve_currency: "USD" | "UZS";
  bonus_approver: Approver;
  budget_alert_threshold: number;
  placement_fee_due_days: number;
  twofa_required_roles: Role[];
  session_hours: number;
  login_max_attempts: number;
  login_lockout_minutes: number;
  expiry_reminder_days: number[];
  retention_check_days: number[];
  apply_form_enabled: boolean;
  apply_default_branch: number | null;
  carrier_portal_show_phone: boolean;
  company_name: string;
  document_types: DocumentType[];
  documents_block_submit: boolean;
  documents_ocr_enabled: boolean;
  documents_ocr_kinds: string[];
  /** Muammoli driverlar qoidalari (apps/drivers/problems.py) */
  driver_required_fields: string[];
  driver_required_from_status: string;
  documents_expiring_days: number;
  /** Saqlashda majburiy (forma) va yo'qotish sababi majburiymi */
  driver_form_required_fields: string[];
  lost_reason_required: boolean;
  version: number;
}

export type SettingsPatch = Partial<Omit<AppSettingsFull, "version">> & { version: number };

export interface AuditActor {
  id: number;
  display_name: string;
  initials: string;
}

export interface AuditEntry {
  id: number;
  created_at: string;
  actor: AuditActor | null;
  actor_label: string;
  action: string;
  model: string;
  object_id: string;
  object_repr: string;
  changes: Record<string, unknown>;
  request_id: string;
  ip: string | null;
  /** Yozuv ochiladigan sahifa (backend: driver, ariza -> driver, hujjat, truck -> carrier, xodim); yo'q bo'lsa null */
  link?: string | null;
}

export const AUDIT_MODELS: { value: string; label: string }[] = [
  { value: "", label: "All objects" },
  { value: "accounts.user", label: "User" },
  { value: "accounts.branch", label: "Branch" },
  { value: "drivers.driver", label: "Driver" },
  { value: "drivers.application", label: "Application" },
  { value: "carriers.carrier", label: "Carrier" },
  { value: "carriers.job", label: "Job" },
  { value: "approvals.approval", label: "Approval" },
  { value: "appsettings.setting", label: "Settings" },
  { value: "budget.expense", label: "Expense" },
  { value: "budget.salary", label: "Salary" },
  { value: "budget.bonus", label: "Bonus" },
];

/** "drivers.driver" -> "Driver" */
export function modelLabel(model: string): string {
  if (!model) return "Auth";
  const found = AUDIT_MODELS.find((m) => m.value === model);
  if (found) return found.label;
  const short = model.split(".").pop() ?? model;
  return short.charAt(0).toUpperCase() + short.slice(1);
}

export const TIMEZONES = ["Asia/Tashkent", "America/Chicago", "America/New_York", "America/Denver", "America/Los_Angeles", "UTC"];
